import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  CreateRolePayload,
  PermissionMatrixGroup,
  RoleDetail,
  RoleListItem,
  RolePermission,
  UpdateRolePayload,
} from "../types";

const ROLES_KEY = ["roles-admin"] as const;

export function usePermissionModules(): UseQueryResult<PermissionMatrixGroup[]> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [...ROLES_KEY, "permission-modules", tokenKey],
    enabled: !!accessToken,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await apiFetch<{ groups: PermissionMatrixGroup[] }>(
        "/roles/permission-modules",
        { token: accessToken },
      );
      return res.groups;
    },
  });
}

export function useRoleList(): UseQueryResult<RoleListItem[]> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [...ROLES_KEY, "list", tokenKey],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ roles: RoleListItem[] }>("/roles", {
        token: accessToken,
      });
      return res.roles;
    },
  });
}

export function useRole(id: string | undefined): UseQueryResult<RoleDetail> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [...ROLES_KEY, "detail", tokenKey, id],
    enabled: Boolean(id) && !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ role: RoleDetail }>(`/roles/${id}`, {
        token: accessToken,
      });
      return res.role;
    },
  });
}

export function useRolePermissions(
  id: string | undefined,
): UseQueryResult<RolePermission[]> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [...ROLES_KEY, "permissions", tokenKey, id],
    enabled: Boolean(id) && !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ permissions: RolePermission[] }>(
        `/roles/${id}/permissions`,
        { token: accessToken },
      );
      return res.permissions;
    },
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (payload: CreateRolePayload) => {
      const res = await apiFetch<{ role: RoleDetail }>("/roles", {
        method: "POST",
        body: payload,
        token: accessToken,
      });
      return res.role;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}

export function useUpdateRole(id: string) {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (payload: UpdateRolePayload) => {
      const res = await apiFetch<{ role: RoleDetail }>(`/roles/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      });
      return res.role;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (id: string) =>
      apiFetch<{ success: true }>(`/roles/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}

export function useUpdateRolePermissions(id: string) {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (permissions: RolePermission[]) => {
      const res = await apiFetch<{ permissions: RolePermission[] }>(
        `/roles/${id}/permissions`,
        {
          method: "PUT",
          body: { permissions },
          token: accessToken,
        },
      );
      return res.permissions;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
}
